import React, {useEffect, useState} from 'react';
import {SubmitHandler, useFieldArray, useForm} from 'react-hook-form';
import {useNavigate, useParams} from 'react-router-dom';
import ImageUploader from './ImageUploader';
import Select from 'react-select';
import {Option, OptionCategory, PriceConfiguration, ProductFormData, UploadedImage} from '../helper/types';
import {createProduct, getCategories, getProductByDocumentId, getSubcategories, updateProduct} from '../helper/CMSApi';
import {categoryToOption} from '../helper/mapper';
import {toast, ToastContainer} from 'react-toastify';

/** Utility for cartesian product */
function cartesian<T>(arrays: T[][]): T[][] {
    return arrays.reduce(
        (acc, curr) => acc.flatMap(a => curr.map(b => [...a, b])),
        [[]] as T[][]
    );
}

function toFormOptionCategories(option_categories): OptionCategory[] {
    return option_categories.map((optionCategory): OptionCategory => {
        return {
            id: optionCategory.id,
            name: optionCategory.name,
            options: optionCategory.options.map(option => {
                return {
                    default: optionCategory.defaultOption.id === option.id,
                    id: option.id,
                    value: option.name
                };
            })
        };
    });
}

function toFormPriceConfigurations(price_configurations, option_categories): PriceConfiguration[] {
    return price_configurations.map(configuration => {
        const priceId = configuration.selectedOptions.map(opt => opt.id).sort().join('-');
        return {
            id: priceId,
            price: configuration.price,
            combination: configuration.selectedOptions.map(opt => {
                return {
                    id: opt.id,
                    value: opt.name,
                    categoryName: option_categories
                        .filter(cat => cat.options.includes(opt => opt.id === opt.id))
                        .map(cat => cat.name)
                };
            })
        };
    });
}

const ProductForm: React.FC = () => {
    const {documentId} = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(documentId);

    /**
     * Add 'rating' to your ProductFormData default values
     * so it starts at 0 if not provided.
     */
    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
        watch,
        control,
        reset
    } = useForm<ProductFormData>({
        defaultValues: {
            image: null,
            hoverImage: null,
            images: [],
            customerPhotos: [],
            category: null,
            subcategory: null,
            dimensions: {
                diameter: 0,
                units: 'mm',
                height: 0,
                width: 0,
                length: 0
            },
            materials: [],
            configuration: {price_configurations: [], option_categories: []},
            characteristics: '',
            popular: false,
            description: '',
            productNumber: '',
            name: '',
            /**
             * Rating default
             */
            rating: 0
        }
    });

    const {
        fields: materialsFields,
        append: appendMaterial,
        remove: removeMaterial
    } = useFieldArray({
        control,
        name: 'materials'
    });

    const [categories, setCategories] = useState<Option[]>([]);
    const [subcategories, setSubcategories] = useState<Option[]>([]);
    const [optionCategories, setOptionCategories] = useState<OptionCategory[]>([]);
    const [priceConfigurations, setPriceConfigurations] = useState<PriceConfiguration[]>([]);
    const selectedCategory = watch('category');
    const [isSubcategoryDisabled, setSubcategoryDisabled] = useState(true);
    const [characteristicsPairs, setCharacteristicsPairs] = useState<
        Array<{ key: string; value: string }>
    >([]);

    // ---------------------
    // Fetch categories
    // ---------------------
    useEffect(() => {
        const fetchData = async () => {
            const categoriesData = await getCategories('');
            setCategories(categoriesData.data.map((cat: any) => categoryToOption(cat)));
        };
        fetchData();
    }, []);

    // ---------------------
    // If category changes, load subcategories
    // ---------------------
    useEffect(() => {
        const updateSubcat = async () => {
            if (selectedCategory) {
                setSubcategoryDisabled(false);
                await updateSubcategoriesSelect('');
            }
        };
        updateSubcat();
    }, [selectedCategory]);

    const updateSubcategoriesSelect = async (input: string) => {
        if (!selectedCategory) return;
        const subcategoriesData = await getSubcategories(input, selectedCategory.value);
        setSubcategories(subcategoriesData.data.map((cat: any) => categoryToOption(cat)));
    };

    // ---------------------
    // Edit mode: load existing product
    // ---------------------
    useEffect(() => {
        if (isEditMode && documentId) {
            getProductByDocumentId(documentId)
                .then(product => {
                    let pairs: Array<{ key: string; value: string }> = [];
                    if (product.data.characteristics) {
                        try {
                            const parsed = product.data.characteristics;
                            pairs = Object.entries(parsed).map(([k, v]) => ({
                                key: k,
                                value: String(v)
                            }));
                            setCharacteristicsPairs(pairs);
                        } catch (err) {
                            console.error('Failed to parse characteristics JSON:', err);
                        }
                    }
                    delete product.data.dimensions.id;

                    // If the product has a rating, set it; otherwise, default is 0
                    const formData = {
                        ...product.data,
                        category: categoryToOption(product.data.category),
                        subcategory: categoryToOption(product.data.subcategory),
                        materials: product.data.materials.map(m => ({name: m.name})),
                        characteristics: product.data.characteristics || '{}',
                        rating: product.data.rating || 0
                    };

                    reset(formData);

                    if (product.data.configuration?.option_categories) {
                        setOptionCategories(toFormOptionCategories(product.data.configuration.option_categories));
                    }
                    if (product.data.configuration?.price_configurations) {
                        setPriceConfigurations(
                            toFormPriceConfigurations(
                                product.data.configuration.price_configurations,
                                product.data.configuration.option_categories
                            )
                        );
                    }
                })
                .catch(err => console.error('Failed to load product:', err));
        }
    }, [isEditMode, documentId, reset]);

    // ---------------------
    // Option Category / Price Config logic
    // ---------------------
    const addCategory = () => {
        const newCategory: OptionCategory = {
            id: Date.now().toString(),
            name: '',
            options: []
        };
        setOptionCategories(prev => [...prev, newCategory]);
    };

    const removeCategory = (catIndex: number) => {
        setOptionCategories(prev => {
            const copy = [...prev];
            copy.splice(catIndex, 1);
            return copy;
        });
    };

    const updateCategoryName = (catIndex: number, newName: string) => {
        setOptionCategories(prev => {
            const copy = [...prev];
            copy[catIndex].name = newName;
            return copy;
        });
    };

    const addOption = (catIndex: number) => {
        setOptionCategories(prev =>
            prev.map((cat, index) =>
                index === catIndex
                    ? {
                        ...cat,
                        options: [
                            ...cat.options,
                            {id: Date.now().toString(), value: '', default: false}
                        ]
                    }
                    : cat
            )
        );
    };

    const updateOption = (catIndex: number, optionIndex: number, newValue: string) => {
        setOptionCategories(prev => {
            const copy = [...prev];
            copy[catIndex].options[optionIndex].value = newValue;
            return copy;
        });
    };

    const removeOption = (catIndex: number, optionIndex: number) => {
        setOptionCategories(prev => {
            const copy = [...prev];
            copy[catIndex].options.splice(optionIndex, 1);
            return copy;
        });
    };

    const selectDefaultOption = (catIndex: number, optionId: string) => {
        setOptionCategories(prev =>
            prev.map((cat, index) => {
                if (index === catIndex) {
                    return {
                        ...cat,
                        options: cat.options.map(opt => ({
                            ...opt,
                            default: opt.id === optionId
                        }))
                    };
                }
                return cat;
            })
        );
    };

    const handleAddCharacteristic = () => {
        setCharacteristicsPairs(prev => [...prev, {key: '', value: ''}]);
    };

    const handleRemoveCharacteristic = (index: number) => {
        setCharacteristicsPairs(prev => {
            const copy = [...prev];
            copy.splice(index, 1);
            return copy;
        });
    };

    const handleChangeCharacteristic = (
        index: number,
        field: 'key' | 'value',
        newVal: string
    ) => {
        setCharacteristicsPairs(prev => {
            const copy = [...prev];
            copy[index][field] = newVal;
            return copy;
        });
    };

    useEffect(() => {
        if (optionCategories.length === 0) {
            setPriceConfigurations([]);
            return;
        }
        const arrays = optionCategories.map(cat =>
            cat.options
                .filter(opt => opt.value.trim() !== '')
                .map(opt => ({
                    ...opt,
                    categoryName: cat.name || 'Unnamed'
                }))
        );

        if (arrays.some(arr => arr.length === 0)) {
            setPriceConfigurations([]);
            return;
        }

        const combinations = cartesian(arrays);
        const newConfigurations = combinations.map(combo => {
            const combinationKey = combo.map(item => item.id).sort().join('-');
            const existing = priceConfigurations.find(pc => pc.id === combinationKey);
            return {
                id: combinationKey,
                combination: combo,
                price: existing ? existing.price : 0
            };
        });
        setPriceConfigurations(newConfigurations);
    }, [optionCategories]);

    // Custom validation registrations
    useEffect(() => {
        register('category', {
            required: 'Category is required',
            validate: value => (value ? true : 'Category must not be null')
        });
        register('subcategory', {
            required: 'Subcategory is required',
            validate: value => (value ? true : 'Subcategory must not be null')
        });
        register('image', {
            required: 'Main image is required',
            validate: value => (value ? true : 'Main image cannot be null')
        });
        register('hoverImage', {
            required: 'Hover image is required',
            validate: value => (value ? true : 'Hover image cannot be null')
        });
        register('images', {
            validate: (value: UploadedImage[]) =>
                value && value.length > 0 ? true : 'At least one image is required'
        });
    }, [register]);

    const handlePriceChange = (index: number, newPrice: number) => {
        setPriceConfigurations(prev => {
            const copy = [...prev];
            copy[index].price = newPrice;
            return copy;
        });
    };

    // ---------------------
    // SUBMIT
    // ---------------------
    const onSubmit: SubmitHandler<ProductFormData> = async data => {
        try {
            data.configuration = {
                option_categories: optionCategories,
                price_configurations: priceConfigurations
            };

            const characteristicsObj = characteristicsPairs.reduce((acc, pair) => {
                if (pair.key.trim()) {
                    acc[pair.key.trim()] = pair.value.trim();
                }
                return acc;
            }, {} as Record<string, string>);

            data.characteristics = JSON.stringify(characteristicsObj);

            if (priceConfigurations.length === 0) {
                toast.error(
                    `Нужна хотя бы одна ценовая конфигурация - создайте категорию с одной опцией и укажите цену`
                );
                return;
            }

            // Check default option in each option category
            const categoriesWithNoDefault = optionCategories.filter(
                category => category.options.find(opt => opt.default === true) == null
            );
            if (categoriesWithNoDefault.length > 0) {
                toast.error(
                    `Option categories ${categoriesWithNoDefault
                        .map(c => c.name)
                        .join(', ')} do not have a default value`
                );
                return;
            }

            for (const pc of priceConfigurations) {
                if (pc.price <= 0) {
                    toast.error(
                        `Price must be > 0 for combination: ${pc.combination
                            .map(c => c.value)
                            .join(', ')}`
                    );
                    return;
                }
            }

            if (!isEditMode) {
                await createProduct(data);
                toast.success('Product created successfully');
                navigate('/');
            } else if (documentId) {
                await updateProduct(documentId, data);
                toast.success('Product updated successfully');
            }
        } catch (error) {
            console.error('Error submitting product:', error);
        }
    };

    return (
        <div>
            <ToastContainer position="top-right" autoClose={3000}/>
            <button
                className="absolute left-10 top-10 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
                onClick={() => navigate('/', {replace: true})}
            >
                Back to menu
            </button>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="max-w-4xl mx-auto p-6 space-y-6 bg-gray-800 text-white shadow rounded"
            >
                <div className="flex flex-row gap-10 mb-4">
                    <h1 className="text-2xl font-bold">
                        {isEditMode ? 'Edit Product' : 'Create Product'}
                    </h1>
                </div>

                {/* Category Selection */}
                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">Категория</label>
                    <Select
                        className="w-full"
                        options={categories}
                        value={categories.find(opt => opt.value === watch('category')?.value) || null}
                        onChange={option => setValue('category', option || null)}
                        isClearable
                        placeholder="Категория"
                    />
                    {errors.category && (
                        <p className="text-red-500">{errors.category.message}</p>
                    )}
                </div>

                {/* Subcategory Selection */}
                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">Подкатегория</label>
                    <Select
                        isDisabled={isSubcategoryDisabled}
                        className="w-full"
                        options={subcategories}
                        value={
                            subcategories.find(opt => opt.value === watch('subcategory')?.value) || null
                        }
                        onChange={option => setValue('subcategory', option || null)}
                        isClearable
                        placeholder="Подкатегория"
                    />
                    {errors.subcategory && (
                        <p className="text-red-500">{errors.subcategory.message}</p>
                    )}
                </div>

                {/* Product Name */}
                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">Имя продукта</label>
                    <input
                        {...register('name', {
                            required: 'Name is required',
                        })}
                        className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                    />
                    {errors.name && <p className="text-red-500">{errors.name.message}</p>}
                </div>
                {/* Popular Checkbox */}
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        {...register('popular')}
                        className="rounded accent-blue-500"
                    />
                    <label className="text-gray-300 font-medium">Popular</label>
                </div>

                {/* Description */}
                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">Описание</label>
                    <textarea
                        {...register('description')}
                        className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                    />
                </div>

                {/* Product Number */}
                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">Артикул</label>
                    <input
                        {...register('productNumber', {
                            required: 'Product number is required',
                        })}
                        className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                    />
                    {errors.productNumber && (
                        <p className="text-red-500">{errors.productNumber.message}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">Rating</label>
                    <input
                        type="number"
                        step="any"
                        {...register('rating', {
                            valueAsNumber: true, // ensures we store it as a number
                            min: {
                                value: 0,
                                message: 'Minimum rating is 0'
                            }
                        })}
                        className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                        placeholder="0"
                    />
                    {errors.rating && (
                        <p className="text-red-500">{errors.rating.message}</p>
                    )}
                </div>

                {/* Dimensions */}
                <div className="space-y-2">
                    <h3 className="block text-gray-300 font-medium">Dimensions</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-300">Diameter</label>
                            <input
                                type="number"
                                {...register('dimensions.diameter', {valueAsNumber: true})}
                                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-300">Units</label>
                            <select
                                {...register('dimensions.units')}
                                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                            >
                                <option value="mm">mm</option>
                                <option value="cm">cm</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-300">Height</label>
                            <input
                                type="number"
                                {...register('dimensions.height', {valueAsNumber: true})}
                                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-300">Width</label>
                            <input
                                type="number"
                                {...register('dimensions.width', {valueAsNumber: true})}
                                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-300">Length</label>
                            <input
                                type="number"
                                {...register('dimensions.length', {valueAsNumber: true})}
                                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                            />
                        </div>
                    </div>
                </div>

                {/* Materials */}
                <div className="space-y-2">
                    <h3 className="block text-gray-300 font-medium">Materials</h3>
                    {materialsFields.map((field, index) => (
                        <div key={field.id} className="flex items-center space-x-2">
                            <input
                                type="text"
                                {...register(`materials.${index}.name` as const, {
                                    required: 'Material name is required',
                                })}
                                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                            />
                            {errors.materials?.[index]?.name && (
                                <p className="text-red-500">
                                    {errors.materials?.[index]?.name?.message as string}
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={() => removeMaterial(index)}
                                className="text-red-500"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => appendMaterial({name: ''})}
                        className="text-blue-400"
                    >
                        Add Material
                    </button>
                </div>


                {/* Multiple images */}
                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">Фотографии</label>
                    <div className="border border-gray-600 rounded p-3">
                        <ImageUploader
                            initialImages={watch('images') ? watch('images') : []}
                            onUpload={(imgs) => {
                                setValue('images', imgs, {shouldValidate: true});
                            }}
                        />
                    </div>
                    {errors.images && (
                        <p className="text-red-500">{errors.images.message}</p>
                    )}
                </div>


                {/* "image" Uploader */}
                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">Главное фото</label>
                    <div className="border border-gray-600 rounded p-3">
                        <ImageUploader
                            initialImages={watch('image') ? [watch('image')] : []}
                            onUpload={(img) => {
                                setValue('image', img[0], {shouldValidate: true});
                            }}
                        />
                    </div>
                    {errors.image && (
                        <p className="text-red-500">{errors.image.message}</p>
                    )}
                </div>

                {/* "hoverImage" Uploader */}
                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">Фото при наведении</label>
                    <div className="border border-gray-600 rounded p-3">
                        <ImageUploader
                            initialImages={watch('hoverImage') ? [watch('hoverImage')] : []}
                            onUpload={(img) => {
                                setValue('hoverImage', img[0], {shouldValidate: true});
                            }}
                        />
                    </div>
                    {errors.hoverImage && (
                        <p className="text-red-500">{errors.hoverImage.message}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">Фото клиентов</label>
                    <div className="border border-gray-600 rounded p-3">
                        <ImageUploader initialImages={watch('customerPhotos') ? watch('customerPhotos') : []}
                                       onUpload={img => setValue('customerPhotos', img)}/>
                    </div>
                </div>

                {/* Characteristics (human‐friendly approach) */}
                <div className="space-y-2">
                    <label className="block text-gray-300 font-medium">
                        Характеристики (Key-Value)
                    </label>
                    {characteristicsPairs.map((pair, index) => (
                        <div key={index} className="flex space-x-2 items-center mb-2">
                            <input
                                type="text"
                                value={pair.key}
                                onChange={(e) => handleChangeCharacteristic(index, 'key', e.target.value)}
                                placeholder="Key"
                                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2"
                            />
                            <input
                                type="text"
                                value={pair.value}
                                onChange={(e) => handleChangeCharacteristic(index, 'value', e.target.value)}
                                placeholder="Value"
                                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2"
                            />
                            <button
                                type="button"
                                onClick={() => handleRemoveCharacteristic(index)}
                                className="text-red-500"
                            >
                                &times;
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={handleAddCharacteristic}
                        className="text-blue-400"
                    >
                        + Add Characteristic
                    </button>
                </div>

                {/* Configuration Section */}
                <div className="space-y-6">
                    <h2 className="text-xl font-bold">Configuration</h2>

                    <div>
                        <h3 className="text-lg font-semibold">Option Categories</h3>
                        {optionCategories.map((cat, catIndex) => (
                            <div key={cat.id} className="p-4 border border-gray-600 rounded mb-4">
                                <input
                                    type="text"
                                    value={cat.name}
                                    onChange={e => updateCategoryName(catIndex, e.target.value)}
                                    placeholder="Category name (e.g., Color)"
                                    className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 w-full"
                                />
                                <div className="mt-2 space-y-2">
                                    {cat.options.map((opt, optIndex) => (
                                        <div key={opt.id} className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                value={opt.value}
                                                onChange={e => updateOption(catIndex, optIndex, e.target.value)}
                                                placeholder="Option (e.g., pink)"
                                                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 flex-1"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeOption(catIndex, optIndex)}
                                                className="text-red-500"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => addOption(catIndex)}
                                    className="mt-2 text-blue-400"
                                >
                                    Add Option
                                </button>
                                <button
                                    type="button"
                                    onClick={() => removeCategory(catIndex)}
                                    className="mt-2 ml-2 text-red-500"
                                >
                                    Remove Category
                                </button>

                                <div className="mt-4">
                                    <h4 className="font-medium">Select default:</h4>
                                    <div className="flex items-center space-x-4">
                                        {cat.options.map(opt => (
                                            <label key={opt.id} className="flex items-center space-x-1">
                                                <input
                                                    type="radio"
                                                    name={`default-${cat.id}`}
                                                    checked={!!opt.default}
                                                    onChange={() => selectDefaultOption(catIndex, opt.id)}
                                                    className="accent-blue-500"
                                                />
                                                <span>{opt.value || 'No Value'}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <button type="button" onClick={addCategory} className="text-blue-400">
                            Add Option Category
                        </button>
                    </div>

                    <div className="mt-6">
                        <h3 className="text-lg font-semibold">Price Configurations</h3>
                        {priceConfigurations.length === 0 ? (
                            <p className="text-gray-400">
                                Please add complete option categories (with at least one option each) to see
                                price configurations
                            </p>
                        ) : (
                            priceConfigurations.map((pc, index) => (
                                <div key={pc.id} className="flex items-center space-x-4 mt-2">
                <span className="w-1/2">
                  {pc.combination
                      .map(item => `${item.categoryName}: ${item.value}`)
                      .join(' | ')}
                </span>
                                    <input
                                        type="number"
                                        value={pc.price}
                                        onChange={e => handlePriceChange(index, Number(e.target.value))}
                                        placeholder="Price"
                                        className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2"
                                    />
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <button
                    type="submit"
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                >
                    {isEditMode ? 'Update Product' : 'Create Product'}
                </button>
            </form>
        </div>
    );
};

export default ProductForm;
